import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkOffIcon from '@mui/icons-material/LinkOff'

// The one set of sharing controls (#1578 item 12). The row popover on DataViz,
// the Sharing tab of dashboard settings, and the web app publish dialog all
// compose these, so a dashboard is shared the same way wherever it is shared.

// #962 Obs 3 — shown verbatim when "Anyone with the link" is selected.
export const PUBLIC_SHARE_WARNING =
  '⚠️ This dashboard will be visible to anyone who has this link — no login or password required. Before sharing, please confirm this is intentional. If this dashboard contains client or commercially sensitive data, we recommend using password protection instead. If any Gen AI Insights cards are set to auto-refresh, each refresh will consume your organisation\'s API tokens — including requests made by anyone who opens this link.'

export type ShareAccessLevel = 'customer' | 'password' | 'public'

export const DEFAULT_SHARE_ACCESS_LEVEL: ShareAccessLevel = 'customer'

export const shareUrlFor = (shareId: string) => `${window.location.origin}/dashboard/${shareId}`

/**
 * A password is needed only when switching to password protection: a
 * dashboard that already has one keeps it when the field is left blank (the
 * share routes keep the stored password for a blank one, 13 Oct).
 */
export const sharePasswordMissing = (accessLevel: string, password: string, hasPassword: boolean) =>
  accessLevel === 'password' && !password.trim() && !hasPassword

interface ShareAccessFieldsProps {
  accessLevel: string
  onAccessLevelChange: (accessLevel: ShareAccessLevel) => void
  password: string
  onPasswordChange: (password: string) => void
  /** The dashboard is already password protected: blank keeps that password. */
  hasPassword?: boolean
}

/** Who can open the link, the public warning, and the password field. */
export const ShareAccessFields: React.FC<ShareAccessFieldsProps> = ({
  accessLevel,
  onAccessLevelChange,
  password,
  onPasswordChange,
  hasPassword,
}) => (
  <>
    <FormControl fullWidth size="small">
      <InputLabel id="share-access-level-label">Access Level</InputLabel>
      <Select
        labelId="share-access-level-label"
        value={accessLevel}
        onChange={(e) => onAccessLevelChange(e.target.value as ShareAccessLevel)}
        label="Access Level"
      >
        <MenuItem value="customer">Organization - Logged-in users only</MenuItem>
        <MenuItem value="password">Password Protected</MenuItem>
        <MenuItem value="public">Anyone with the link</MenuItem>
      </Select>
    </FormControl>

    {accessLevel === 'public' && (
      <Alert severity="warning" icon={false}>
        {PUBLIC_SHARE_WARNING}
      </Alert>
    )}

    {accessLevel === 'password' && (
      <TextField
        fullWidth
        size="small"
        type="password"
        label="Password"
        value={password}
        onChange={(e) => onPasswordChange(e.target.value)}
        placeholder={hasPassword ? 'Leave blank to keep current' : ''}
        helperText={hasPassword ? 'Leave blank to keep the current password.' : 'Required: the password people type to open the link.'}
      />
    )}
  </>
)

interface ShareLinkFieldProps {
  shareId: string
  /** Omitted: no Disable sharing button. */
  onDisable?: () => void
  disabled?: boolean
}

/** The share link with a copy button, and Disable sharing. */
export const ShareLinkField: React.FC<ShareLinkFieldProps> = ({ shareId, onDisable, disabled }) => {
  const [copied, setCopied] = useState(false)
  const url = shareUrlFor(shareId)

  const handleCopy = () => {
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        fullWidth
        size="small"
        value={url}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Copy link" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        helperText={copied ? 'Copied!' : undefined}
      />
      {onDisable && (
        <Button
          size="small"
          color="error"
          startIcon={<LinkOffIcon />}
          onClick={onDisable}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start' }}
        >
          Disable Sharing
        </Button>
      )}
    </Box>
  )
}
